import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  CreditCard,
  CheckCircle,
  AlertCircle,
  Calendar,
  Zap,
  Settings,
  Clock,
  Shield,
  DollarSign,
  Star,
  Award,
  Users,
  Bell,
  X,
  Key,
  Eye,
  EyeOff,
  Copy
} from 'lucide-react';
import LicenseModal from '../../components/LicenseModal';
import VerifyEmailModal from '../../components/VerifyEmailModal';
import { useAuth } from '../../context/AuthContext';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/api';
import stripeService from '../../services/stripe';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  const isVerified = user?.IsEmailVerified === true || user?.isEmailVerified === true;

  // Helper function to determine subscription data from license status
  const determineSubscriptionFromLicense = (licenseStatus) => {
    if (licenseStatus.hasPaidLicense && licenseStatus.subscriptionType) {
      const license = licenseStatus.paidLicense;
      const expirationDate = new Date(license.expirationDate);
      
      // Parse subscription type from backend (e.g., "Professional - Monthly", "Professional - Annual")
      const subscriptionParts = licenseStatus.subscriptionType.split(' - ');
      const rawTier = subscriptionParts.length > 1 ? subscriptionParts[1] : 'Unknown';

      // Normalize backend duration strings to display names
      const tierDisplayMap = {
        '1 month': 'Monthly',
        '3 months': 'Quarterly',
        '6 months': 'Semi-Annual',
        '1 year': 'Annual'
      };
      const tier = tierDisplayMap[rawTier] || rawTier;
      
      // Determine billing display based on tier
      let billing = 'Custom Plan';
      if (tier === 'Monthly') {
        billing = '$119.99/month';
      } else if (tier === 'Quarterly') {
        billing = '$329.99/quarter';
      } else if (tier === 'Semi-Annual') {
        billing = '$599.99/6 months';
      } else if (tier === 'Annual') {
        billing = '$999.99/year';
      }
      
      return {
        plan: 'pro',
        status: licenseStatus.subscriptionStatus || 'active',
        nextBillingDate: expirationDate.toISOString(),
        paymentMethod: {
          type: 'credit_card',
          last4: '****', // We don't have this info from license
          expiryMonth: null,
          expiryYear: null
        },
        tier: tier,
        billing: billing,
        subscriptionType: licenseStatus.subscriptionType,
        features: [
          'Full professional features',
          'Priority support',
          'Advanced reporting',
          'Unlimited customers',
          'Export capabilities',
          'Custom workflows'
        ]
      };
    }
    
    // Default to free plan if no paid license
    return {
      plan: 'free',
      status: 'active',
      nextBillingDate: null,
      paymentMethod: null,
      tier: 'Free',
      billing: '$0.00 - Free Plan',
      subscriptionType: null,
      features: [
        'Basic shop management',
        'Customer database',
        'Basic reporting',
        'Community support'
      ]
    };
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [hasNotified, setHasNotified] = useState(false);
  const [showActivationKey, setShowActivationKey] = useState(false);
  const [activationKey, setActivationKey] = useState('');
  const [keycopied, setKeyCopied] = useState(false);
  const [showTrialKey, setShowTrialKey] = useState(false);
  const [trialKey, setTrialKey] = useState('');
  const [trialExpiration, setTrialExpiration] = useState(null);
  const [trialKeyCopied, setTrialKeyCopied] = useState(false);
  const [, setLicenses] = useState({ trialLicense: null, paidLicenses: [] });
  const keyCopiedTimeoutRef = useRef(null);
  const trialKeyCopiedTimeoutRef = useRef(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (keyCopiedTimeoutRef.current) {
        clearTimeout(keyCopiedTimeoutRef.current);
      }
      if (trialKeyCopiedTimeoutRef.current) {
        clearTimeout(trialKeyCopiedTimeoutRef.current);
      }
    };
  }, []);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [showNewLicenseModal, setShowNewLicenseModal] = useState(false);
  const [newPurchasedLicense, setNewPurchasedLicense] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState({
    hasPaidLicense: false,
    trialLicense: null,
    paidLicense: null,
    canPurchaseLicense: true
  });
  const [subscription, setSubscription] = useState({
    plan: 'free',
    status: 'active',
    nextBillingDate: null,
    paymentMethod: null,
    features: []
  });
  const [selectedTier, setSelectedTier] = useState('monthly');
  
  // Stripe price IDs mapping
  const stripePriceIds = {
    monthly: 'price_1TDB9kHqnsvGU54Y22QY9l9Q',
    quarterly: 'price_1TDB9iHqnsvGU54Yb2MPIJ0X',
    semiannual: 'price_1TDB9jHqnsvGU54YYrNL6rLg',
    annual: 'price_1TDB9kHqnsvGU54YZLwdptgp'
  };

  // Mock subscription plans
  const plans = [
    {
      id: 'pro',
      name: 'Stonewall Invoicing',
      price: '$119.99',
      frequency: 'per month',
      color: 'bg-blue-100 dark:bg-blue-900',
      features: [
        'Single User License',
        'Unlimited Invoices',
        'Advanced Customer Management',
        'Complete Vehicle Database with FREE yearly updates',
        'Comprehensive Reports and Analytics',
        'Email Notifications and Reminders',
        'Easy PDF Invoice Generation'
      ],
      icon: Star,
      cta: subscription.plan === 'pro' ? 'Current Plan' : 'Choose Plan',
      disabled: subscription.plan === 'pro',
      current: subscription.plan === 'pro',
      description: 'Our flagship product provides a complete solution for your automotive service business.',
      priceOptions: [
        { duration: 'Monthly', price: '$119.99/mo' },
        { duration: '3 Months', price: '$109.99/mo', savings: 'Save $30' },
        { duration: '6 Months', price: '$99.99/mo', savings: 'Save $120' },
        { duration: 'Annual', price: '$999/year', savings: 'Save $440' }
      ]
    },
    {
      id: 'cloud',
      name: 'Stonewall Cloud',
      price: 'Coming Soon',
      frequency: '',
      color: 'bg-indigo-100 dark:bg-indigo-900',
      features: [
        'All Professional features included',
        'Cloud invoice storage and data sync',
        'Real-time synchronization',
        'Cross-device compatibility',
        'Automatic cloud backups'
      ],
      icon: Award,
      cta: 'Notify Me',
      disabled: false,
      current: subscription.plan === 'cloud',
      isCloud: true,
      description: 'Access your business data anytime, anywhere with our cloud-connected version. Collaborate with your team in real-time and enjoy automatic backups and updates.'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        
        if (!user) {
          navigate('/login');
          return;
        }

        // Check for purchase success in URL params
        const urlParams = new URLSearchParams(window.location.search);
        const isSuccessfulPurchase = urlParams.get('success') === 'true';
        
        if (isSuccessfulPurchase) {
          setPurchaseSuccess(true);
          // Remove the success param from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check for recently purchased license and show modal
          try {
            const recentLicenseResponse = await api.get('/License/my-licenses?recentOnly=true');
            if (recentLicenseResponse.data.recentPurchaseLicense) {
              setNewPurchasedLicense(recentLicenseResponse.data.recentPurchaseLicense);
              setShowNewLicenseModal(true);
            }
          } catch (error) {
            console.error('Failed to fetch recent license:', error);
          }
        }
        
        // Load pricing data from Stripe config
        try {
          await stripeService.getPricing();
        } catch (error) {
          console.error('Failed to fetch pricing:', error);
        }

        // Fetch user's license status (for UI display logic)
        try {
          const statusResponse = await api.get('/License/my-license-status');
          setLicenseStatus(statusResponse.data);
          
          // Set subscription data based on actual license status
          const subscriptionData = determineSubscriptionFromLicense(statusResponse.data);
          setSubscription(subscriptionData);

          // Sync the plan selector with the active subscription tier
          const tierToSelectedMap = {
            'Monthly': 'monthly',
            'Quarterly': 'quarterly',
            'Semi-Annual': 'semiannual',
            'Annual': 'annual'
          };
          if (subscriptionData.tier && tierToSelectedMap[subscriptionData.tier]) {
            setSelectedTier(tierToSelectedMap[subscriptionData.tier]);
          }

          // Fetch Stripe subscription details for pending change info
          if (statusResponse.data.hasPaidLicense) {
            try {
              const stripeDetails = await stripeService.getSubscriptionDetails();
              if (stripeDetails?.hasPendingChange) {
                setSubscription(prev => ({
                  ...prev,
                  hasPendingChange: true,
                  pendingPlanTier: stripeDetails.pendingPlanTier,
                  pendingBillingCycle: stripeDetails.pendingBillingCycle,
                  pendingAmount: stripeDetails.pendingAmount,
                  pendingChangeDate: stripeDetails.pendingChangeDate
                }));
              }
            } catch (error) {
              console.error('Failed to fetch subscription details:', error);
            }
          }

          // Set the license keys for display
          if (statusResponse.data.trialLicense) {
            setTrialKey(statusResponse.data.trialLicense.licenseKey);
            setTrialExpiration(statusResponse.data.trialLicense.expirationDate);
          }
          if (statusResponse.data.paidLicense) {
            setActivationKey(statusResponse.data.paidLicense.licenseKey);
          }
        } catch (error) {
          console.error('Failed to fetch license status:', error);
          // Fallback to free plan if license status fetch fails
          const subscriptionData = determineSubscriptionFromLicense({ hasPaidLicense: false, paidLicense: null });
          setSubscription(subscriptionData);
        }

        // Fetch user's licenses (normal flow - all licenses)
        try {
          const licensesResponse = await api.get('/License/my-licenses');
          setLicenses(licensesResponse.data);
        } catch (error) {
          console.error('Failed to fetch licenses:', error);
        }
      } catch (err) {
        setError('Failed to load subscription data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleUpgrade = async (planId, isCloud) => {
    if (planId === 'free_trial') {
      navigate('/downloads');
      return;
    }
    
    if (isCloud) {
      setShowNotifyModal(true);
      return;
    }
    
    // Start Stripe checkout for Professional plan
    if (planId === 'pro') {
      // Check if user can purchase a license
      if (!licenseStatus.canPurchaseLicense) {
        alert('You already have a paid license. Please use the "Manage Subscription" button to view, cancel, or change your current license.');
        return;
      }
      
      try {
        const user = getCurrentUser();
        const priceId = stripePriceIds[selectedTier];
        
        const session = await stripeService.createCheckoutSession(
          priceId,
          user.id,
          `${window.location.origin}/account/subscription?success=true`,
          `${window.location.origin}/account/subscription`
        );
        
        await stripeService.redirectToCheckout(session.sessionId);
      } catch (error) {
        console.error('Error creating checkout:', error);
        if (error.message && error.message.includes('already has a paid license')) {
          alert('You already have a paid license. Please use the "Manage Subscription" button to view, cancel, or change your current license.');
        } else {
          alert('Failed to start checkout. Please try again.');
        }
      }
    }
  };

  const getTierName = (tier) => {
    const names = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Semi-Annual',
      annual: 'Annual'
    };
    return names[tier] || tier;
  };

  const openCustomerPortal = async () => {
    try {
      const user = getCurrentUser();
      const portalData = await stripeService.createCustomerPortalSession(user.id.toString());

      if (portalData.url || portalData) {
        // Handle both response formats: {url: "..."} or direct URL string
        const portalUrl = portalData.url || portalData;

        // Validate URL is from Stripe domain (defense in depth)
        try {
          const url = new URL(portalUrl);
          if (!url.hostname.endsWith('.stripe.com') && url.hostname !== 'stripe.com') {
            throw new Error('Invalid portal URL - not from Stripe');
          }
        } catch (urlError) {
          console.error('Invalid portal URL:', urlError);
          alert('Invalid portal URL received. Please try again or contact support.');
          return;
        }

        window.location.href = portalUrl;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      // Provide more specific error message
      let errorMessage = 'Failed to open customer portal. ';
      if (error.response?.status === 404) {
        errorMessage += 'Customer not found. Please ensure you have an active subscription.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please sign in again.';
      } else {
        errorMessage += 'Please try again or contact support.';
      }

      alert(errorMessage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div className="flex flex-col flex-1 bg-gray-100 dark:bg-gray-900 color-transition">
      <div className="flex-grow container mx-auto px-4 sm:px-5 lg:px-7 py-10">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md mb-6 flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="ml-3">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Purchase Success Banner */}
              {purchaseSuccess && (
                <div className="mb-8 bg-green-50 dark:bg-green-900/30 p-6 rounded-lg border-2 border-green-300 dark:border-green-700 shadow-lg">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800/50 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-700 dark:text-green-300" />
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-1">
                        Payment Successful!
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                        Your purchase has been completed successfully. Your new license key is now available below.
                      </p>
                      <button
                        onClick={() => setPurchaseSuccess(false)}
                        className="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Current subscription overview */}
              <div className="bg-gray-200 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition mb-8">
                <div className="px-6 py-5 bg-gray-300 dark:bg-gray-700 border-b border-gray-400 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                        subscription.plan === 'pro'
                          ? 'bg-blue-100 dark:bg-blue-900/50'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}>
                        {subscription.plan === 'free' ? (
                          <Users className="w-6 h-6 text-gray-800 dark:text-gray-300" />
                        ) : (
                          <Star className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                      <div className="ml-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {subscription.plan === 'free' ? 'Free Plan' : 'Stonewall Professional'}
                        </h2>
                        <p className="text-sm text-gray-700 dark:text-gray-400">
                          {subscription.plan === 'free'
                            ? 'Basic access to Stonewall Invoicing'
                            : `${subscription.tier} Billing Cycle`
                          }
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center ${
                      subscription.status === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : subscription.status === 'canceled'
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        : subscription.status === 'past_due' || subscription.status === 'payment_failed'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {subscription.status === 'active' ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          Active
                        </>
                      ) : subscription.status === 'canceled' ? (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 mr-1" />
                          Cancelled
                        </>
                      ) : subscription.status === 'past_due' ? (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 mr-1" />
                          Past Due
                        </>
                      ) : subscription.status === 'payment_failed' ? (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 mr-1" />
                          Payment Failed
                        </>
                      ) : subscription.status === 'incomplete' ? (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 mr-1" />
                          Incomplete
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          Active
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {/* Cancellation warning */}
                  {subscription.status === 'canceled' && (
                    <div className="mb-6 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-orange-900 dark:text-orange-300">
                            Subscription Cancelled
                          </h3>
                          <p className="mt-1 text-sm text-orange-700 dark:text-orange-400">
                            You'll continue to have access to all professional features until{' '}
                            <span className="font-semibold">{formatDate(subscription.nextBillingDate)}</span>.
                            After this date, your account will be downgraded to the Free plan.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pending plan change notice */}
                  {subscription.hasPendingChange && (
                    <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start">
                        <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                            Plan Change Scheduled
                          </h3>
                          <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                            Your subscription will change to{' '}
                            <span className="font-semibold">{subscription.pendingPlanTier} — {subscription.pendingBillingCycle}</span>
                            {subscription.pendingChangeDate && (
                              <> on <span className="font-semibold">{formatDate(subscription.pendingChangeDate)}</span></>
                            )}.
                            Your current plan remains active until then.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subscription details cards */}
                  {subscription.plan !== 'free' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      {/* Billing Amount */}
                      <div className="bg-gray-300 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-400 dark:border-gray-600">
                        <div className="flex items-center mb-2">
                          <DollarSign className="w-4 h-4 text-gray-700 dark:text-gray-400" />
                          <span className="ml-1.5 text-xs font-medium text-gray-700 dark:text-gray-400 uppercase tracking-wide">Billing</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{subscription.billing}</p>
                        {subscription.tier !== 'Monthly' && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">
                            {subscription.tier === 'Annual' ? 'Save $440/yr vs monthly' :
                             subscription.tier === 'Semi-Annual' ? 'Save $120 vs monthly' :
                             subscription.tier === 'Quarterly' ? 'Save $30 vs monthly' : ''}
                          </p>
                        )}
                      </div>

                      {/* Next Billing / Expiry */}
                      <div className="bg-gray-300 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-400 dark:border-gray-600">
                        <div className="flex items-center mb-2">
                          <Calendar className="w-4 h-4 text-gray-700 dark:text-gray-400" />
                          <span className="ml-1.5 text-xs font-medium text-gray-700 dark:text-gray-400 uppercase tracking-wide">
                            {subscription.status === 'canceled' ? 'Expires' : 'Renews'}
                          </span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {subscription.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A'}
                        </p>
                        {subscription.status === 'canceled' && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-medium">No automatic renewal</p>
                        )}
                      </div>

                      {/* Payment Method */}
                      <div className="bg-gray-300 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-400 dark:border-gray-600">
                        <div className="flex items-center mb-2">
                          <CreditCard className="w-4 h-4 text-gray-700 dark:text-gray-400" />
                          <span className="ml-1.5 text-xs font-medium text-gray-700 dark:text-gray-400 uppercase tracking-wide">Payment</span>
                        </div>
                        {subscription.paymentMethod && subscription.paymentMethod.last4 !== '****' ? (
                          <>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              •••• {subscription.paymentMethod.last4}
                            </p>
                            <p className="text-xs text-gray-700 dark:text-gray-400 mt-1">
                              Expires {subscription.paymentMethod.expiryMonth}/{subscription.paymentMethod.expiryYear}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">Managed via Stripe</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click Manage Subscription to view details</p>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start">
                        <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="ml-3">
                          <p className="text-sm text-blue-800 dark:text-blue-300">
                            <span className="font-medium">Upgrade to Professional</span> to unlock unlimited invoices, advanced reporting, and priority support.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick info strip */}
                  {subscription.plan !== 'free' && (
                    <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-800 dark:text-gray-400">
                      <span className="inline-flex items-center">
                        <Shield className="w-4 h-4 mr-1.5 text-blue-500 dark:text-blue-400" />
                        Single User License
                      </span>
                      <span className="inline-flex items-center">
                        <Zap className="w-4 h-4 mr-1.5 text-blue-500 dark:text-blue-400" />
                        All Professional Features
                      </span>
                      <span className="inline-flex items-center">
                        <Star className="w-4 h-4 mr-1.5 text-blue-500 dark:text-blue-400" />
                        Priority Support
                      </span>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="pt-5 border-t border-gray-400 dark:border-gray-700 flex flex-wrap gap-3">
                    {subscription.plan !== 'free' ? (
                      <>
                        <button
                          onClick={openCustomerPortal}
                          className="inline-flex items-center px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Manage Subscription
                        </button>
                        <Link
                          to="/account/billing-history"
                          className="inline-flex items-center px-4 py-2.5 border border-gray-400 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400/70 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Billing History
                        </Link>
                        <Link
                          to="/account/settings?tab=payment"
                          className="inline-flex items-center px-4 py-2.5 border border-gray-400 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400/70 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Payment Details
                        </Link>
                      </>
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-400">
                        Purchase a Professional license below to access subscription management.
                      </p>
                    )}
                  </div>

                </div>
              </div>
              
              {/* Activation Key Section - IMPORTANT */}
              {activationKey && (
                <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 shadow-md overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                      <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">Your Activation Key</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Use this key to activate Stonewall Invoicing after downloading</p>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <div className="flex items-center space-x-3">
                      <div className="w-full">
                        <div className="relative">
                          <input
                            type={showActivationKey ? "text" : "password"}
                            value={activationKey}
                            readOnly
                            className="w-full px-4 py-3 pr-24 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-lg text-gray-900 dark:text-white"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                            <button
                              onClick={() => setShowActivationKey(!showActivationKey)}
                              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                              title={showActivationKey ? "Hide key" : "Show key"}
                            >
                              {showActivationKey ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(activationKey);
                                setKeyCopied(true);
                                keyCopiedTimeoutRef.current = setTimeout(() => setKeyCopied(false), 2000);
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                              title="Copy to clipboard"
                            >
                              {keycopied ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <Copy className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>Keep this key secure. You'll need it to activate your software.</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Free Trial Banner with Activation Key */}
              {trialKey && (!trialExpiration || new Date(trialExpiration) > new Date()) && (
                <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 shadow-md overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center">
                    <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0">
                      <Key className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">Your 14-Day Free Trial Key</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Use this key to activate the trial version of Stonewall Invoicing</p>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <div className="flex items-center space-x-3">
                      <div className="w-full">
                        <div className="relative">
                          <input
                            type={showTrialKey ? "text" : "password"}
                            value={trialKey}
                            readOnly
                            className="w-full px-4 py-3 pr-24 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-lg text-gray-900 dark:text-white"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                            <button
                              onClick={() => setShowTrialKey(!showTrialKey)}
                              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                              title={showTrialKey ? "Hide key" : "Show key"}
                            >
                              {showTrialKey ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(trialKey);
                                setTrialKeyCopied(true);
                                trialKeyCopiedTimeoutRef.current = setTimeout(() => setTrialKeyCopied(false), 2000);
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                              title="Copy to clipboard"
                            >
                              {trialKeyCopied ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <Copy className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>Trial expires {trialExpiration ? new Date(trialExpiration).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'in 14 days'}. Full access to all premium features included.</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Available plans */}
              <div className="mb-8">

                {/* Pro Plan Card */}
                <div className={`border shadow-md rounded-lg p-6 md:p-8 mb-6 ${
                  licenseStatus.hasPaidLicense
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-300 dark:border-green-700'
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}>

                  {/* Card header row */}
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Stonewall Invoicing</h2>
                        {!licenseStatus.hasPaidLicense && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-0.5 rounded-lg font-semibold">Save up to 31%</span>
                        )}
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Our flagship product provides a complete solution for your automotive service business.</p>
                    </div>
                    {licenseStatus.hasPaidLicense && (
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700">
                          <CheckCircle className="w-4 h-4 mr-1.5" />
                          Active Plan
                        </span>
                        <button
                          onClick={openCustomerPortal}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Settings className="w-4 h-4 mr-1.5" />
                          Manage License
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">

                    {/* Left — Pricing options */}
                    <div>
                      <div className="space-y-2 mb-5">
                        {plans[0].priceOptions.map((option, index) => {
                          const tierKey = option.duration === 'Monthly' ? 'monthly' :
                                         option.duration === '3 Months' ? 'quarterly' :
                                         option.duration === '6 Months' ? 'semiannual' : 'annual';
                          const isAnnual = option.duration === 'Annual';
                          const isSelected = selectedTier === tierKey;
                          const tierToDuration = { 'Monthly': 'Monthly', 'Quarterly': '3 Months', 'Semi-Annual': '6 Months', 'Annual': 'Annual' };
                          const isActiveTier = licenseStatus.hasPaidLicense && tierToDuration[subscription.tier] === option.duration;
                          return (
                            <div
                              key={index}
                              onClick={() => !licenseStatus.hasPaidLicense && setSelectedTier(tierKey)}
                              className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                                licenseStatus.hasPaidLicense ? 'cursor-default' : 'cursor-pointer'
                              } ${
                                isActiveTier
                                  ? 'bg-green-50 dark:bg-green-950/30 border-2 border-green-500 dark:border-green-500'
                                  : licenseStatus.hasPaidLicense
                                    ? 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 opacity-70'
                                    : isSelected
                                      ? 'bg-white dark:bg-gray-700 border-2 border-blue-500 dark:border-blue-400'
                                      : isAnnual
                                        ? 'bg-white dark:bg-gray-700 border-2 border-blue-300 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-400'
                                        : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white">{option.duration}</span>
                                {isActiveTier && (
                                  <span className="text-[11px] bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Current</span>
                                )}
                                {option.savings && (
                                  <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">{option.savings}</span>
                                )}
                              </div>
                              <span className="font-bold text-gray-900 dark:text-white">{option.price}</span>
                            </div>
                          );
                        })}
                      </div>

                      {!licenseStatus.hasPaidLicense && (
                        <>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Selected: <strong className="text-gray-900 dark:text-white">{getTierName(selectedTier)}</strong>
                          </p>
                          <button
                            onClick={() => {
                              if (!isVerified) {
                                setShowVerifyModal(true);
                              } else {
                                handleUpgrade('pro', false);
                              }
                            }}
                            className={`w-full inline-flex justify-center items-center px-4 py-3 rounded-lg text-sm font-semibold transition-colors text-white ${
                              !isVerified ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                          >
                            {!isVerified ? (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Verify Email to Purchase
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Purchase {getTierName(selectedTier)} Subscription
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </div>

                    {/* Right — Features */}
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">What's included</h3>
                      <ul className="space-y-3">
                        {plans[0].features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>
                </div>

                {/* Cloud Plan Card */}
                <div className="bg-gray-900 dark:bg-[#111] rounded-lg p-8 text-white">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="md:max-w-xl">
                      <span className="text-xs uppercase tracking-widest text-blue-400 font-semibold">Coming Soon</span>
                      <h2 className="text-2xl font-bold mt-2 mb-2 tracking-tight">Stonewall Cloud</h2>
                      <p className="text-gray-400 mb-5 text-sm">Access your business data from anywhere. Multi-user support, real-time sync, automatic cloud backups, and cross-device compatibility.</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {plans[1].features.map((item, index) => (
                          <div key={index} className="flex items-center">
                            <svg className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-gray-300">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleUpgrade('cloud', true)}
                        className={`px-8 py-3 rounded-lg text-base font-semibold transition-colors ${
                          hasNotified
                            ? 'bg-green-700 text-white cursor-default'
                            : 'bg-white text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {hasNotified ? (
                          <span className="flex items-center">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Thank You!
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <Bell className="w-4 h-4 mr-2" />
                            Notify Me
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
              
              {/* FAQ Section */}
              <div className="bg-gray-200 dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
                <div className="px-6 py-5 bg-gray-300 dark:bg-gray-700 border-b border-gray-400 dark:border-gray-600">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Frequently Asked Questions</h2>
                </div>
                
                <div className="p-6">
                  <div className="space-y-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">How do I change my plan?</h3>
                        <p className="mt-2 text-sm text-gray-800 dark:text-gray-400">
                          Click on "Choose Plan" to view available subscription options and select the one that best fits your needs. 
                          You can manage your subscription through the Stripe customer portal.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">What happens when I cancel?</h3>
                        <p className="mt-2 text-sm text-gray-800 dark:text-gray-400">
                          When you cancel your subscription through the customer portal, you'll still have access to your paid features until the end of your current billing period.
                          After that, your account will be downgraded to the Free plan.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">How do I update my payment information?</h3>
                        <p className="mt-2 text-sm text-gray-800 dark:text-gray-400">
                          Use the "Manage Subscription" button to access the Stripe customer portal where you can update your payment method, download invoices, and manage your subscription.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">Is my payment information secure?</h3>
                        <p className="mt-2 text-sm text-gray-800 dark:text-gray-400">
                          Yes, all payment processing is handled by Stripe, a PCI-compliant payment processor. Your credit card details are never stored on our servers.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-200 dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Notified About Stonewall Cloud</h3>
              <p className="text-gray-800 dark:text-gray-300">
                Be the first to know when our cloud solution launches. Enter your email below and we'll keep you updated!
              </p>
            </div>
            
            {!hasNotified ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                setHasNotified(true);
              }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </button>
              </div>
            </form>
            ) : (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-800 dark:text-gray-300">
                    We've added {notifyEmail} to our notification list. You'll be the first to know when Stonewall Cloud launches!
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNotifyModal(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* New License Modal - shows only the newly purchased license */}
      <LicenseModal
        isOpen={showNewLicenseModal}
        onClose={() => setShowNewLicenseModal(false)}
        licenseKey={newPurchasedLicense?.licenseKey || ''}
        licenseType="paid"
        expirationDate={newPurchasedLicense?.expirationDate}
      />

      <VerifyEmailModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        userEmail={user?.email || user?.Email}
      />
    </div>
  );
};

export default SubscriptionPage;